import { urlBase64ToUint8Array, VAPID_PUBLIC_KEY } from "@/lib/push/config";

/**
 * Browser-only web-push subscription flow, shared by every opt-in surface
 * (the settings/seeker `PushOptIn` toggle and the post-apply value-moment
 * prompt) so the register → permission → subscribe → store sequence lives in
 * one place instead of being copy-pasted per surface.
 *
 * Registers /sw.js, requests notification permission, subscribes via the
 * PushManager, and POSTs the subscription to /api/push/subscribe. Returns a
 * discriminated result so the caller can drive its own UI/telemetry — this
 * helper never captures analytics or throws (callers own both).
 */
export type PushSubscribeResult =
	| "subscribed"
	| "denied"
	| "dismissed_permission"
	| "error";

export async function subscribeToPush(
	/** Locale of the surface the user subscribed from. Travels with the request
	 * so the server can label a push-only saved search in the user's own
	 * language instead of assuming the default (2026-09-01). */
	locale?: "en" | "id",
): Promise<PushSubscribeResult> {
	try {
		const reg = await navigator.serviceWorker.register("/sw.js");
		await navigator.serviceWorker.ready;
		const permission = await Notification.requestPermission();
		if (permission !== "granted") {
			// "denied" is terminal (browser won't re-prompt); "default" means the
			// user dismissed the prompt without deciding and can be asked again.
			return permission === "denied" ? "denied" : "dismissed_permission";
		}
		const sub = await reg.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
		});
		const res = await fetch("/api/push/subscribe", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ subscription: sub.toJSON(), locale }),
		});
		if (!res.ok) return "error";
		return "subscribed";
	} catch {
		return "error";
	}
}

/**
 * True when this browser can receive web push. Mirrors the guard in
 * `PushOptIn` so a surface can render nothing (never dead-end) on browsers
 * without support (iOS Safari < 16.4, etc.).
 */
export function isPushSupported(): boolean {
	return (
		typeof window !== "undefined" &&
		"serviceWorker" in navigator &&
		"PushManager" in window &&
		"Notification" in window
	);
}
